import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';
import { NativePercentCoordinator, normalizeCode, isActivationContextAllowed, canSkipCheckoutSync, buyerErrorMessage, debugDiagnostic } from './afz-native-percent-core-20261006.js?v=afz-20261006-r5';

const SLOT = '__AFZNativePercent20261006';
const ownEvents = new WeakSet();
const STATE_EVENT = 'afz-native-percent:state';
export const getCoordinator = () => window[SLOT] ?? null;
export const emitOwnEvent = (target, event) => { ownEvents.add(event); (target ?? document).dispatchEvent(event); };

function relevant(cart, config) {
  const productIds = new Set(config.campaigns.map((campaign) => campaign.productId));
  const codes = new Set(config.campaigns.flatMap((campaign) => campaign.nativeRules.map((rule) => normalizeCode(rule.code))));
  return (cart?.items ?? []).some((item) => productIds.has(String(item.product_id))) || (cart?.discount_codes ?? []).some((entry) => codes.has(normalizeCode(typeof entry === 'string' ? entry : entry.code)));
}

function withTimeout(promise, duration = 15000) {
  let timer;
  return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Your bag is taking too long to update. Please try again.')), duration); })]).finally(() => clearTimeout(timer));
}

async function boot() {
  if (window[SLOT]) return;
  const node = document.querySelector('[data-afz-native-percent-registry]');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); }
  catch { return; }
  // Disabled candidate has no listeners, fetches or checkout interception.
  if (!isActivationContextAllowed(config, window.Shopify?.theme)) return;
  const theme = typeof Theme !== 'undefined' ? Theme : window.Theme;
  const root = window.Shopify?.routes?.root ?? '/';
  const routes = {
    cart: theme?.routes?.cart_url ? theme.routes.cart_url + '.js' : root + 'cart.js',
    update: theme?.routes?.cart_update_url ?? root + 'cart/update.js',
    add: theme?.routes?.cart_add_url ?? root + 'cart/add.js',
  };
  const sectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  let channel = null;
  let sourceId = '';
  if (typeof BroadcastChannel !== 'undefined') {
    sourceId = crypto.randomUUID();
    channel = new BroadcastChannel('afz-native-percent-20261006');
  }
  async function request(url, body) {
    if (!isActivationContextAllowed(config, window.Shopify?.theme)) throw new Error('This bundle preview is not available in the current theme.');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Your bag could not be updated. Please review it before checkout.');
      return data;
    } finally { clearTimeout(timer); }
  }
  const core = new NativePercentCoordinator(config, {
    readCart: () => request(routes.cart),
    loadSuppression: (token) => {
      try { const saved = JSON.parse(sessionStorage.getItem('afz-native-percent-suppressed-20261006') ?? 'null'); return saved?.token === token ? saved.productIds.filter((id) => config.campaigns.some((campaign) => campaign.productId === id)) : []; }
      catch { return []; }
    },
    saveSuppression: (token, productIds) => { try { sessionStorage.setItem('afz-native-percent-suppressed-20261006', JSON.stringify({ token, productIds })); } catch { /* Storage is optional; native cart remains authoritative. */ } },
    loadAttempt: (token) => {
      try { const saved = JSON.parse(sessionStorage.getItem('afz-native-percent-attempt-20261006') ?? 'null'); return saved?.token === token ? saved : null; }
      catch { return null; }
    },
    saveAttempt: (attempt) => { try { if (attempt) sessionStorage.setItem('afz-native-percent-attempt-20261006', JSON.stringify(attempt)); else sessionStorage.removeItem('afz-native-percent-attempt-20261006'); } catch { /* The current page still retains its UUID and receipt. */ } },
    withTimeout,
    withLock: (task) => navigator.locks?.request ? navigator.locks.request('afz-native-percent-20261006', task) : task(),
    writeDiscounts: async (codes) => {
      if (codes.length > 5) throw new Error('Your bag has too many discount codes. Your existing codes were kept.');
      const deferred = CartDiscountUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      emitOwnEvent(document, new CartDiscountUpdateEvent({ discountCodes: codes.map((code) => ({ code })), promise: deferred.promise }));
      try {
        const cart = await request(routes.update, { discount: codes.join(','), sections: sectionIds().join(','), sections_url: window.location.pathname });
        deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: cart.sections, source: 'afz-native-percent-20261006', didError: false } });
        channel?.postMessage({ sourceId, type: 'mutation' });
        return cart;
      } catch (error) { deferred.reject(error); throw error; }
    },
    addItems: async (items) => {
      const added = await request(routes.add, { items, sections: sectionIds().join(','), sections_url: window.location.pathname });
      channel?.postMessage({ sourceId, type: 'mutation' });
      return added;
    },
    onState: (state) => document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: state })),
    onVerified: (cart, summary) => document.dispatchEvent(new CustomEvent('afz-native-percent:verified', { detail: { cart, summary } })),
  });
  window[SLOT] = core;
  core.stateEvent = STATE_EVENT;
  core.emitOwnEvent = emitOwnEvent;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!ownEvents.has(event)) core.observeMutation(event.promise, { kind: 'lines' });
  }, true);
  document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
    if (!ownEvents.has(event)) core.observeMutation(event.promise, { kind: 'discount', discountCodes: event.discountCodes ?? event.data?.discountCodes });
  }, true);
  document.addEventListener(StandardEvents.cartNoteUpdate, (event) => {
    if (!ownEvents.has(event)) core.observeMutation(event.promise, { kind: 'note' });
  }, true);
  channel?.addEventListener('message', (event) => {
    if (event.data?.sourceId !== sourceId && event.data?.type === 'mutation') core.sync('other-tab-mutation').catch(() => {});
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') core.sync('page-visible').catch(() => {});
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) core.sync('history-restored').catch(() => {}); });

  let navigating = false;
  const replay = new WeakSet();
  const showCheckoutError = (form, error) => {
    const scope = form ?? document.querySelector('cart-items-component');
    if (!scope) return;
    let status = scope.querySelector('[data-afz-native-percent-checkout-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'afz-bundle__status';
      status.dataset.afzNativePercentCheckoutStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      scope.append(status);
    }
    debugDiagnostic(error, 'ordinary-checkout');
    status.textContent = buyerErrorMessage(error);
  };
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || replay.has(form)) { if (form instanceof HTMLFormElement) replay.delete(form); return; }
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout' && submitter?.id !== 'checkout') return;
    if (canSkipCheckoutSync(core, relevant(core.lastCart, config))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (navigating) return;
    navigating = true;
    try {
      await core.prepareCheckout();
      const liveForm = form.isConnected ? form : document.getElementById(form.id || 'cart-form');
      const liveButton = submitter?.isConnected ? submitter : document.getElementById(submitter?.id || 'checkout');
      if (!(liveForm instanceof HTMLFormElement) || !(liveButton instanceof HTMLElement) || liveButton.disabled) throw new Error('Your bag changed. Please press checkout again.');
      replay.add(liveForm);
      liveForm.requestSubmit(liveButton);
    } catch (error) { showCheckoutError(form.isConnected ? form : document.getElementById('cart-form'), error); }
    finally { navigating = false; }
  }, true);
  document.addEventListener('click', async (event) => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || anchor.target === '_blank') return;
    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin || !/\/(?:[a-z]{2}(?:-[a-z]{2})\/)?checkout\/?$/i.test(url.pathname)) return;
    if (canSkipCheckoutSync(core, relevant(core.lastCart, config))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (navigating) return;
    navigating = true;
    try { await core.prepareCheckout(); window.location.assign(url.href); }
    catch (error) { showCheckoutError(null, error); navigating = false; }
  }, true);
  // Wallet/accelerated checkout elements are deliberately untouched.
  core.sync('initial-cart').catch(() => {});
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot().catch(() => {}), { once: true });
else boot().catch(() => {});
